import { cn } from '@utils/cn';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { TFunction } from 'next-i18next';
import { useTranslation } from 'next-i18next';
import { useId, useState } from 'react';

import { Image } from '../../../components/Image';
import { BannerCard } from '../Cards';
import { Icon } from '../Icon';
import styles from './framing.module.css';
import { FrameTabs } from './FrameTabs';

export interface FrameContentSection {
  id: string;
  /** Tab label, e.g. "Framing". */
  label: string;
  items: Array<{ title: string; description: string }>;
  image: { src: string; alt: string };
}

export interface FrameContentCta {
  title: string;
  href: string;
  image: { filename: string; alt?: string; focus?: string };
}

export interface FrameContentProps {
  /** One tab per section. Defaults to the built-in Framing / Mounting / Glazing content. */
  sections?: FrameContentSection[];
  /** Link card. Defaults to the framing studio card; `null` hides it. */
  cta?: FrameContentCta | null;
  defaultSectionId?: string;
  className?: string;
}

const SECTION_IMAGES = {
  framing: '/images/framing/craftsmanship-framing.png',
  mounting: '/images/framing/craftsmanship-mounting.png',
  glazing: '/images/framing/craftsmanship-glazing.png',
} as const;

const STUDIO_IMAGE = '/images/framing/craftsmanship-studio.png';
const STUDIO_HREF = '/insights/guides/our-framing-studio';

const getItems = (t: TFunction, key: string): FrameContentSection['items'] => {
  const value = t(key, { returnObjects: true }) as unknown;
  return Array.isArray(value)
    ? (value as FrameContentSection['items']).filter(
        (item) => typeof item?.title === 'string',
      )
    : [];
};

const getDefaultSections = (t: TFunction): FrameContentSection[] =>
  (Object.keys(SECTION_IMAGES) as Array<keyof typeof SECTION_IMAGES>).map(
    (id) => ({
      id,
      label: t(`craftsmanship.${id}.label`),
      items: getItems(t, `craftsmanship.${id}.items`),
      image: {
        src: SECTION_IMAGES[id],
        alt: t(`craftsmanship.${id}.imageAlt`),
      },
    }),
  );

/**
 * The "Our approach to craftsmanship" body: tabbed sections of short feature
 * copy, a supporting image per section and a link to the framing studio. The
 * section heading is left to the page. Renders the built-in content by
 * default; `sections` and `cta` override it.
 */
export const FrameContent = ({
  className,
  cta: ctaProp,
  defaultSectionId,
  sections: sectionsProp,
}: FrameContentProps) => {
  const { t } = useTranslation('framing');
  const idPrefix = useId();
  const shouldReduceMotion = useReducedMotion();
  const sections = sectionsProp ?? getDefaultSections(t);
  const cta =
    ctaProp === undefined
      ? {
          title: t('craftsmanship.cta'),
          href: STUDIO_HREF,
          image: {
            filename: STUDIO_IMAGE,
            alt: t('craftsmanship.studioImageAlt'),
          },
        }
      : ctaProp;
  const [activeId, setActiveId] = useState(
    defaultSectionId ?? sections[0]?.id ?? '',
  );
  const active =
    sections.find((section) => section.id === activeId) ?? sections[0];

  if (!active) return null;

  return (
    <div className={cn(styles.UiFrameContent, 'grid gap-5', className)}>
      {sections.length > 1 ? (
        <FrameTabs
          activeKey={active.id}
          idPrefix={idPrefix}
          onChange={setActiveId}
          tabs={sections.map(({ id, label }) => ({ key: id, label }))}
        />
      ) : null}

      <div
        aria-labelledby={
          sections.length > 1 ? `${idPrefix}-tab-${active.id}` : undefined
        }
        className={cn(
          'grid gap-5 md:gap-5',
          cta ? 'md:grid-cols-3' : 'md:grid-cols-2',
        )}
        id={`${idPrefix}-panel-${active.id}`}
        role={sections.length > 1 ? 'tabpanel' : undefined}
      >
        <ul className="grid content-start">
          {active.items.map((item) => (
            <li key={item.title} className={styles.UiFrameContent__item}>
              <h3 className="font-bold">{item.title}</h3>
              <p>{item.description}</p>
            </li>
          ))}
        </ul>

        {/* Switching tabs crossfades the images: both share one grid cell
            while the old fades out and the new fades in. */}
        <div className="grid overflow-hidden rounded-sm">
          <AnimatePresence initial={false}>
            <motion.div
              key={active.id}
              animate={{ opacity: 1 }}
              className="[grid-area:1/1]"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{
                duration: shouldReduceMotion ? 0 : 0.3,
                ease: 'easeOut',
              }}
            >
              <Image
                alt={active.image.alt}
                size={{
                  sm: { ratio: '1/1', span: 12 },
                  md: { ratio: '1/1', span: 4 },
                }}
                src={active.image.src}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {cta ? (
          <div className={styles.UiFrameContent__cta}>
            <BannerCard
              as="a"
              aspectRatioClassName="aspect-square"
              href={cta.href}
              image={cta.image}
              title={cta.title}
              variant="image"
            />
            <span aria-hidden className={styles.UiFrameContent__arrow}>
              <Icon name="ic_arrow_top_right" />
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};
